import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import CallToActionBanner from "../components/CallToActionBanner";
import QuoteForm from "../components/QuoteForm";
import SEO from "../components/SEO";
import { polymerContent } from "../mock";
import { Package, FileText, Boxes, Layers, CheckCircle2, ArrowLeft, Info } from "lucide-react";

function PolymerBlock({ polymer, disclaimer }) {
  return (
    <article id={polymer.code} className="scroll-mt-28 fade-in">
      {/* Header row */}
      <div className="flex items-start gap-4 mb-6">
        <span
          className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center font-display font-bold text-white text-sm"
          style={{ background: "var(--brand-700)" }}
          data-testid={`polymer-code-${polymer.code.toLowerCase()}`}
        >
          {polymer.code}
        </span>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
            {polymer.code} · Commodity Plastic Resin
          </p>
          <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight" style={{ color: "var(--brand-700)" }}>
            {polymer.name}
          </h3>
        </div>
      </div>

      <p className="text-foreground/80 leading-relaxed max-w-4xl">{polymer.description}</p>

      {/* Polymer product images strip (pellets + supplier bags) */}
      {polymer.image && (
        <div className="mt-6 rounded-2xl border border-border overflow-hidden bg-muted/20 shadow-sm">
          <img
            src={polymer.image}
            alt={`${polymer.name} — resin pellets and supplier packaging`}
            className="w-full h-auto block"
            loading="lazy"
            data-testid={`polymer-image-${polymer.code.toLowerCase()}`}
          />
        </div>
      )}

      {/* Typical Application vs Product Characteristics */}
      <div className="mt-8 rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
          <Layers size={17} style={{ color: "var(--brand-700)" }} />
          <h4 className="font-display font-semibold" style={{ color: "var(--brand-700)" }}>
            Typical Application & Product Characteristics
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-6 py-3 w-1/2">Typical Application</th>
                <th className="px-6 py-3 w-1/2">Product Characteristics</th>
              </tr>
            </thead>
            <tbody>
              {polymer.applications.map((row, ri) => (
                <tr key={ri} className="border-b border-border last:border-0 hover:bg-muted/30 transition">
                  <td className="px-6 py-3 text-foreground/80 leading-relaxed align-top">
                    {row.app === "—" ? <span className="text-muted-foreground/50">—</span> : row.app}
                  </td>
                  <td className="px-6 py-3 font-medium leading-relaxed align-top" style={{ color: "var(--brand-700)" }}>
                    {row.char}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Physical Properties */}
      <div className="mt-8 rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
          <FileText size={17} style={{ color: "var(--brand-700)" }} />
          <h4 className="font-display font-semibold" style={{ color: "var(--brand-700)" }}>Physical Properties</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-6 py-3">Property</th>
                <th className="px-6 py-3">Test Method</th>
                <th className="px-6 py-3">Typical Value</th>
                <th className="px-6 py-3">Unit</th>
              </tr>
            </thead>
            <tbody>
              {polymer.physicalGroups.map((grp) => (
                <React.Fragment key={grp.group}>
                  <tr style={{ background: "var(--brand-50)" }}>
                    <td colSpan={4} className="px-6 py-2 text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--brand-700)" }}>
                      {grp.group}
                    </td>
                  </tr>
                  {grp.rows.map((row, ri) => (
                    <tr key={ri} className="border-b border-border last:border-0 hover:bg-muted/30 transition">
                      <td className="px-6 py-3 font-medium text-foreground">{row.property}</td>
                      <td className="px-6 py-3 text-foreground/70 font-mono text-[13px]">{row.test}</td>
                      <td className="px-6 py-3 font-semibold" style={{ color: "var(--brand-700)" }}>{row.value}</td>
                      <td className="px-6 py-3 text-foreground/70 font-mono text-[13px]">{row.unit}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {polymer.technicalNote && (
        <div className="mt-4 p-4 rounded-lg border border-border flex items-start gap-3" style={{ background: "var(--brand-50)" }}>
          <Info size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--brand-700)" }} />
          <p className="text-sm text-foreground/80 leading-relaxed">
            <span className="font-semibold" style={{ color: "var(--brand-700)" }}>Technical note: </span>{polymer.technicalNote}
          </p>
        </div>
      )}
      <p className="mt-3 text-xs italic text-muted-foreground leading-relaxed max-w-4xl">
        {disclaimer}
      </p>

      {/* Packaging */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <Boxes size={18} style={{ color: "var(--brand-700)" }} />
          <h4 className="font-display text-lg font-semibold" style={{ color: "var(--brand-700)" }}>Product Packaging</h4>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {polymer.packaging.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-border bg-muted/20">
              <CheckCircle2 size={18} style={{ color: "var(--brand-500)" }} />
              <p className="mt-2 text-sm font-medium" style={{ color: "var(--brand-700)" }}>{p}</p>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function PolymerDetail() {
  const [active, setActive] = useState(polymerContent.polymers[0].code);

  return (
    <>
      <SEO
        title="Commodity Plastic Resin — PP · HDPE · LDPE · PS · PET · PVC · EVA"
        description="Ruken Alazdehar supplies commodity plastic resins: PP, HDPE, LDPE, PS, PET, PVC and EVA — sourced from major petrochemical producers with full technical data sheets and certificates of analysis."
        path="/products/commodity-plastic-resin"
      />
      <PageHero
        title={polymerContent.title}
        subtitle={polymerContent.description}
        breadcrumbs={[
          { label: "Commodities", to: "/product-categories" },
          { label: "Petroleum Products", to: "/products/category-f-petroleum-products" },
          { label: "Commodity Plastic Resin" },
        ]}
        bgImage={polymerContent.image}
      />

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <Link to="/products/category-f-petroleum-products" className="inline-flex items-center gap-2 text-sm font-semibold link-underline" style={{ color: "var(--brand-700)" }}>
              <ArrowLeft size={14} /> Back to Petroleum Products
            </Link>
          </div>

          {/* Intro */}
          <div className="max-w-4xl mb-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--brand-100)" }}>
                <Package size={16} style={{ color: "var(--brand-700)" }} />
              </span>
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Products in this Guide
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight" style={{ color: "var(--brand-700)" }}>
              PP · HDPE · LDPE · PS · PET · PVC · EVA
            </h2>
            <p className="mt-5 text-foreground/80 leading-relaxed max-w-3xl">{polymerContent.intro}</p>
          </div>

          {/* Polymer tab navigator */}
          <div className="sticky top-24 z-30 -mx-6 px-6 py-3 mb-10 backdrop-blur-md bg-white/95 border-y border-border">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold flex-shrink-0" style={{ color: "var(--brand-500)" }}>
                Jump to:
              </span>
              {polymerContent.polymers.map((p) => (
                <a
                  key={p.code}
                  href={`#${p.code}`}
                  onClick={() => setActive(p.code)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition whitespace-nowrap ${
                    active === p.code ? "text-white" : "hover:border-brand"
                  }`}
                  style={
                    active === p.code
                      ? { backgroundColor: "var(--brand-700)", borderColor: "var(--brand-700)" }
                      : { borderColor: "var(--border)", color: "var(--brand-700)" }
                  }
                  data-testid={`polymer-tab-${p.code.toLowerCase()}`}
                >
                  <span className="font-bold">{p.code}</span>
                  <span className="hidden md:inline">· {p.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* All polymers stacked */}
          <div className="space-y-16 md:space-y-20">
            {polymerContent.polymers.map((p) => (
              <PolymerBlock key={p.code} polymer={p} disclaimer={polymerContent.disclaimer} />
            ))}
          </div>
        </div>
      </section>

      <CallToActionBanner />

      <section id="inquiry" className="section-padding bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Get a Quote</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>Commodity Plastic Resin Inquiry</h2>
            <p className="mt-3 text-muted-foreground">Share your grade, tonnage and destination — our team will respond within one business day.</p>
          </div>
          <QuoteForm specPlaceholder="e.g. HDPE film grade, MFI 0.30 g/10min, 25 kg bag" />
        </div>
      </section>
    </>
  );
}
